/* Focused fork of osrs-sdk GLTFModel: retained actor/model split, GLTF mixer,
 * mesh instances and world-coordinate conversion. Semantic clips replace export
 * order indices; absolute virtual time replaces mixer update and animation callbacks. */
import * as THREE from 'three';
import type {Model,Location3} from '../vendor/osrs-sdk/Model';
import type {Renderable} from '../vendor/osrs-sdk/Renderable';
import type {AssetProvider,ModelAsset} from '../assets/provider';
import {AssetAnimation} from '../assets/animation';
import {centerOf,facingToward} from '../engine/geometry';
import {interpolatedTile,movementFacing,isMoving} from './pose';
import type {Entity,Player} from '../engine/types';
export class EntityModel implements Model{
 readonly group=new THREE.Group();private asset:ModelAsset;
 private parts=new Map<string,{asset:ModelAsset;animation:AssetAnimation}>();
 constructor(readonly adapter:EntityAdapter,readonly provider:AssetProvider){
  const e=adapter.entity;
  this.asset=provider.model(e.kind==='player'?'player.body':`npc.${e.kind}`);
  this.group.userData.entityId=e.id;
  if(e.kind!=='player')this.addPart(`npc.${e.kind}`,this.asset);
 }
 private addPart(id:string,asset:ModelAsset){
  asset.object.scale.setScalar(asset.scale);asset.object.position.set(...asset.offset);if(asset.rotation)asset.object.rotation.set(...asset.rotation);
  asset.object.traverse(o=>{o.userData.entityId=this.adapter.entity.id;});
  this.group.add(asset.object);
  this.parts.set(id,{asset,animation:new AssetAnimation(asset.object,asset.animations,asset.clips)});
 }
 private removePart(id:string){
  const part=this.parts.get(id);if(!part)return;
  part.animation.destroy();this.group.remove(part.asset.object);
  if(part.asset.fallback)part.asset.object.traverse(o=>{if(o instanceof THREE.Mesh){o.geometry.dispose();for(const m of Array.isArray(o.material)?o.material:[o.material])m.dispose();}});
  this.parts.delete(id);
 }
 draw(scene:THREE.Scene,_delta:number,alpha:number,location:Location3,angle:number,_pitch:number,visible:boolean){
  if(this.group.parent!==scene)scene.add(this.group);const e=this.adapter.entity;
  this.group.visible=visible;this.group.position.set(location.x+e.size/2,location.z,location.y-e.size/2);this.group.rotation.y=angle;
  if(e.kind==='player'){
   const equipped=Object.values((e as Player).equipment).filter(Boolean).map(s=>s!.id);
   const ids=this.provider.playerModelIds(equipped);
   for(const id of this.parts.keys())if(!ids.includes(id))this.removePart(id);
   for(const id of ids)if(!this.parts.has(id))this.addPart(id,this.provider.model(id));
  }
  const animation=e.animation,t=Math.max(0,(this.adapter.tick+alpha-animation.start)*.6);
  for(const part of this.parts.values()){
   part.animation.pose(animation,e.kind,this.adapter.tick+alpha);
   if(part.asset.fallback){this.asset=part.asset;this.fallbackPose(animation.id,t);}
  }
 }
 private fallbackPose(id:string,t:number){
  const g=this.asset.object,e=this.adapter.entity;
  g.rotation.set(0,0,0);g.position.y=0;const left=g.getObjectByName('frontLeft'),right=g.getObjectByName('frontRight');
  if(left)left.rotation.x=0;if(right)right.rotation.x=0;
  if(id==='jad.magic'){
   const lift=Math.min(1,t/.35)*(t<1.55?1:Math.max(0,1-(t-1.55)/.45));g.rotation.x=-.25*lift;g.position.y=.25*lift;if(left)left.rotation.x=-1.05*lift;if(right)right.rotation.x=-1.05*lift;
  }else if(id==='jad.range'){
   const lift=t<.25?t/.25:t<.55?1:Math.max(0,1-(t-.55)/.16);if(left)left.rotation.x=-.7*lift;if(right)right.rotation.x=-.7*lift;g.position.y=t>.55&&t<.85?-.18:0;
  }else if(id==='jad.melee'){if(left)left.rotation.x=-Math.sin(Math.min(t/ .5,1)*Math.PI)*1.4;}
  else if(id.includes('death')){g.rotation.x=Math.min(1,t/1.6)*Math.PI/2;g.position.y=-Math.min(1,t/2)*.4;}
  else if(id.includes('walk')||id.includes('run')){
   const phase=Math.sin(t*(id.includes('run')?18:10))*.55;
   for(const name of ['legLeft','legRight']){const part=g.getObjectByName(name);if(part)part.rotation.x=name==='legLeft'?phase:-phase;}
   if(left)left.rotation.x=phase;if(right)right.rotation.x=-phase;
  }else if(e.kind==='player'&&!id.includes('idle')){if(left)left.rotation.x=-1;if(right)right.rotation.x=-1.2;}
  else if(id==='healer.heal'||id==='healer.melee'){if(left)left.rotation.x=-1.25;if(right)right.rotation.x=-1.25;}
 }
 destroy(scene:THREE.Scene){scene.remove(this.group);for(const id of [...this.parts.keys()])this.removePart(id);}
 getWorldPosition(){return this.group.getWorldPosition(new THREE.Vector3());}
 async preload(){}
}
export class EntityAdapter implements Renderable{
 model:EntityModel;tick=0;lookTarget:Entity|null=null;
 constructor(public entity:Entity,provider:AssetProvider){this.model=new EntityModel(this,provider);}
 get3dModel(){return this.model;}
 getPerceivedLocation(alpha:number){return {...interpolatedTile(this.entity,alpha),z:0};}
 getPerceivedRotation(alpha=1){
  const e=this.entity,target=this.lookTarget;
  if(e.hp>0&&target?.hp)return facingToward(centerOf(e,interpolatedTile(e,alpha)),centerOf(target,interpolatedTile(target,alpha)),e.facing);
  return isMoving(e)?movementFacing(e,alpha):e.facing;
 }
 getPerceivedPitch(){return 0;}
 getPerceivedOffsets(){return [];}
 visible(){return this.entity.deadAt===null||this.tick-this.entity.deadAt<6;}
 shouldDestroy(){return !this.visible();}
}
